import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { 
  UserPlus, 
  FileText, 
  CheckCircle, 
  Briefcase, 
  Calendar, 
  Hammer, 
  TrendingUp, 
  CheckSquare, 
  Mail, 
  Archive,
  ArrowRight
} from 'lucide-react';

interface WorkflowStage {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  labelKey: string;
  count: number;
  value: number;
}

interface WorkflowStatistics {
  lead: { count: number; value: number };
  createQuote: { count: number; value: number };
  sendQuote: { count: number; value: number };
  quoteAccepted: { count: number; value: number };
  jobCreated: { count: number; value: number };
  planJob: { count: number; value: number };
  doWork: { count: number; value: number };
  monitorProgress: { count: number; value: number };
  completeJob: { count: number; value: number };
  generateInvoice: { count: number; value: number };
  sendInvoice: { count: number; value: number };
  closeJob: { count: number; value: number };
}

interface WorkflowProcessWidgetProps {
  color: string;
}

export const WorkflowProcessWidget: React.FC<WorkflowProcessWidgetProps> = ({ color }) => {
  const { t } = useTranslation();
  const [statistics, setStatistics] = useState<WorkflowStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchWorkflowStatistics();
  }, []);

  const fetchWorkflowStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/dashboard-settings/workflow-statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch workflow statistics');
      }

      const data = await response.json();
      if (data.success) {
        setStatistics(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch workflow statistics');
      }
    } catch (err) {
      console.error('Error fetching workflow statistics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load workflow statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="text-xs text-error-600 dark:text-error-400">{error}</div>
      </div>
    );
  }

  if (!statistics) {
    return null;
  }

  const workflowStages: WorkflowStage[] = [
    { id: 'lead', icon: UserPlus, labelKey: 'processFlow.lead', count: statistics.lead.count, value: statistics.lead.value },
    { id: 'createQuote', icon: FileText, labelKey: 'processFlow.createQuote', count: statistics.createQuote.count, value: statistics.createQuote.value },
    { id: 'quoteAccepted', icon: CheckCircle, labelKey: 'processFlow.quoteAccepted', count: statistics.quoteAccepted.count, value: statistics.quoteAccepted.value },
    { id: 'jobCreated', icon: Briefcase, labelKey: 'processFlow.jobCreated', count: statistics.jobCreated.count, value: statistics.jobCreated.value },
    { id: 'planJob', icon: Calendar, labelKey: 'processFlow.planJob', count: statistics.planJob.count, value: statistics.planJob.value },
    { id: 'doWork', icon: Hammer, labelKey: 'processFlow.doWork', count: statistics.doWork.count, value: statistics.doWork.value },
    { id: 'completeJob', icon: CheckSquare, labelKey: 'processFlow.completeJob', count: statistics.completeJob.count, value: statistics.completeJob.value },
    { id: 'sendInvoice', icon: Mail, labelKey: 'processFlow.sendInvoice', count: statistics.sendInvoice.count, value: statistics.sendInvoice.value },
    { id: 'closeJob', icon: Archive, labelKey: 'processFlow.closeJob', count: statistics.closeJob.count, value: statistics.closeJob.value }
  ];

  // Calculate total pipeline value
  // Note: jobCreated, planJob, and doWork represent the same items in different states
  // We should only count each unique item once. The backend duplicates these values,
  // so we'll use the maximum value from overlapping stages to avoid double-counting.
  const totalValue = 
    statistics.lead.value +
    statistics.createQuote.value +
    statistics.quoteAccepted.value +
    Math.max(statistics.jobCreated.value, statistics.planJob.value, statistics.doWork.value) + // Same items, take max
    statistics.completeJob.value +
    statistics.sendInvoice.value +
    statistics.closeJob.value;
  
  const totalCount = 
    statistics.lead.count +
    statistics.createQuote.count +
    statistics.quoteAccepted.count +
    Math.max(statistics.jobCreated.count, statistics.planJob.count, statistics.doWork.count) + // Same items, take max
    statistics.completeJob.count +
    statistics.sendInvoice.count +
    statistics.closeJob.count;

  return (
    <div className="w-full h-full flex flex-col min-h-0">
      {/* Modern Header */}
      <div className="mb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">Sales Pipeline</h3>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">Pipeline overview</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-bold text-gray-900 dark:text-white">
              ${totalValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">{totalCount} items</div>
          </div>
        </div>
      </div>

      {/* Modern Pipeline Stages */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden min-h-0">
        <div className="flex gap-3 pb-2 h-full items-end">
          {workflowStages.map((stage, index) => {
            const Icon = stage.icon;
            const isLast = index === workflowStages.length - 1;
            
            return (
              <div key={stage.id} className="flex items-end gap-3 flex-shrink-0">
                <div className="flex flex-col items-center gap-2 min-w-[80px]">
                  {/* Stage Card */}
                  <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 border border-gray-200 dark:border-gray-700 overflow-hidden group">
                    {/* Icon Header */}
                    <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-2 flex items-center justify-center">
                      <div className="relative">
                        <Icon className="w-4 h-4 text-accent-600 dark:text-accent-400" />
                        <span className="absolute -top-1 -right-1 text-[8px] font-bold text-accent-600 dark:text-accent-400 bg-white dark:bg-gray-800 rounded-full w-3.5 h-3.5 flex items-center justify-center">
                          {index + 1}
                        </span>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-2 space-y-1.5">
                      <div className="text-[10px] font-semibold text-gray-900 dark:text-gray-100 text-center leading-tight line-clamp-2">
                        {t(stage.labelKey)}
                      </div>
                      
                      {/* Metrics */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-xs font-bold text-gray-900 dark:text-white">
                            {stage.count}
                          </span>
                          <span className="text-[9px] text-gray-500 dark:text-gray-400">items</span>
                        </div>
                        <div className={`text-[10px] font-bold text-center ${
                          stage.value > 0 ? 'text-success-600 dark:text-success-400' : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          ${stage.value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Arrow Connector */}
                {!isLast && (
                  <div className="flex items-center pb-4">
                    <ArrowRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
